import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Container, Paper, Stack, Typography, Box, Divider,
  Button, TextField, Alert, IconButton, InputAdornment,
  CircularProgress, Link, Backdrop
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { 
  signInWithEmailAndPassword, 
  sendEmailVerification,
  signOut,
  Auth,
  User
} from "firebase/auth";
import { auth as defaultAuth } from '../firebase';
import { logEvent } from '../analytics';

interface PasswordLoginPageProps {
  authInstance?: Auth;
  onLoginSuccess?: () => void;
  initialUnverifiedUser?: User | null;
}

const getFirebaseErrorMessage = (error: any): string => {
  const code = error?.code;
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      return 'Invalid email or password. Please double-check your credentials.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/missing-email':
      return 'Please enter your email address.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    case 'auth/unauthorized-domain':
      return 'This domain is not authorized for authentication in Firebase Console.';
    default:
      return error?.message || 'Authentication failed. Please try again.';
  }
};

const PasswordLoginPage: React.FC<PasswordLoginPageProps> = ({ 
  authInstance, 
  onLoginSuccess, 
  initialUnverifiedUser 
}) => {
  const auth = authInstance || defaultAuth;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [unverifiedUser, setUnverifiedUser] = useState<User | null>(initialUnverifiedUser || null);

  React.useEffect(() => {
    document.title = "Jets '14 | Sign in with Password";
  }, []);

  const handleAuthResult = async (user: User) => {
    setLoading(true);
    try {
      await user.reload();
      if (!user.emailVerified) {
        setLoading(false);
        setUnverifiedUser(user);
        return;
      }
      setLoading(false);
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    } catch (e) {
      setLoading(false);
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    }
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }

    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email.trim(), password);
      logEvent('login', { method: 'Email_Password' });
      await handleAuthResult(userCredential.user);
    } catch (err: any) {
      console.error("Email sign-in error:", err);
      setError(getFirebaseErrorMessage(err));
      logEvent('login_error', { method: 'Email_Password', error: err?.code });
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedUser) return;
    setLoading(true);
    setError(null);
    try {
      await sendEmailVerification(unverifiedUser);
      setMessage(`Verification email resent to ${unverifiedUser.email}! Please check your inbox.`);
    } catch (err: any) {
      setError(err.message || 'Failed to resend verification email.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationCheck = async () => {
    if (!unverifiedUser) return;
    setLoading(true);
    setError(null);
    try {
      await unverifiedUser.reload();
      if (unverifiedUser.emailVerified) {
        setUnverifiedUser(null);
        await handleAuthResult(unverifiedUser);
      } else {
        setLoading(false);
        setError('Email is still not verified. Please check your inbox and click the verification link.');
      }
    } catch (err: any) {
      setLoading(false);
      setError('Failed to check verification status. Please try again.');
    }
  };

  const handleCancelVerification = async () => {
    await signOut(auth);
    setUnverifiedUser(null);
    setError(null);
    setMessage(null);
  };

  // Verification Screen
  if (unverifiedUser) {
    return (
      <Container component="main" maxWidth="xs">
        <Paper 
          elevation={3}
          sx={{
            marginTop: { xs: 3, sm: 6 },
            marginBottom: { xs: 3, sm: 6 },
            padding: { xs: 2.5, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 2,
          }}
        >
          <Box sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', p: 1.5, borderRadius: '50%', mb: 2 }}>
            <MarkEmailReadIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>

          <Typography component="h1" variant="h5" textAlign="center" fontWeight="bold" gutterBottom>
            Verify Your Email
          </Typography>
          <Typography variant="body2" textAlign="center" sx={{ mb: 2, color: 'text.secondary' }}>
            We've sent a verification link to <strong>{unverifiedUser.email}</strong>. 
            Please click the link in that email to verify your account before accessing the team portal.
          </Typography>
          
          {error && <Alert severity="error" sx={{ mt: 1, mb: 2, width: '100%' }}>{error}</Alert>}
          {message && <Alert severity="success" sx={{ mt: 1, mb: 2, width: '100%' }}>{message}</Alert>}

          <Button
            fullWidth
            variant="contained"
            disableElevation
            onClick={handleVerificationCheck}
            disabled={loading}
            sx={{ mt: 2, mb: 1, py: 1 }}
          >
            {loading ? <CircularProgress size={24} /> : "I've verified my email"}
          </Button>

          <Button
            fullWidth
            variant="outlined"
            onClick={handleResendVerification}
            disabled={loading}
            sx={{ mb: 1, py: 0.8 }}
          >
            Resend Verification Email
          </Button>

          <Button
            fullWidth
            onClick={handleCancelVerification}
            color="inherit"
            size="small"
            sx={{ mt: 1 }}
          >
            Back to Sign In
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Paper 
        elevation={3}
        sx={{
          marginTop: { xs: 3, sm: 6 },
          marginBottom: { xs: 3, sm: 6 },
          padding: { xs: 2.5, sm: 4 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: 2,
        }}
      >
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Box
            component="img"
            src={process.env.PUBLIC_URL + '/chicago_jets_shield_logo.jpg'} 
            alt="Chicago Jets Logo" 
            sx={{ height: { xs: 65, sm: 80 }, width: 'auto' }} 
          />
          <Box
            component="img"
            src={process.env.PUBLIC_URL + '/csdhl_logo.png'} 
            alt="CSDHL Logo" 
            sx={{ height: { xs: 65, sm: 80 }, width: 'auto' }} 
          />
        </Stack>
        
        <Typography 
          component="h1" 
          variant="h5" 
          textAlign="center" 
          fontWeight="bold"
          sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem' }, mb: 1 }}
        >
          Sign in with Password
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {error}
          </Alert>
        )}

        {message && (
          <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
            {message}
          </Alert>
        )}

        {/* Email & Password Form */}
        <Box component="form" onSubmit={handleEmailSignIn} noValidate sx={{ width: '100%', mt: 0.5 }}>
          <TextField
            margin="dense"
            required
            fullWidth
            id="password-signin-email"
            label="Email Address"
            name="email"
            type="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            size="small"
          />
          <TextField
            margin="dense"
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            id="password-signin-password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <Box textAlign="right" sx={{ mt: 0.5, mb: 1.5 }}>
            <Link 
              component={RouterLink}
              to="/forgot-password"
              variant="caption"
            >
              Forgot password?
            </Link>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            startIcon={<LockOutlinedIcon />}
            sx={{ mb: 2, py: 1, textTransform: 'none', fontWeight: 600 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Sign In'}
          </Button>

          <Divider sx={{ width: '100%', my: 1.5 }} />

          <Stack spacing={1} sx={{ width: '100%', textAlign: 'center', mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              First time logging in?{' '}
              <Link component={RouterLink} to="/create-password" sx={{ fontWeight: 500 }}>
                Create a password
              </Link>
            </Typography>
            <Typography variant="body2">
              <Link component={RouterLink} to="/" color="text.secondary" sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                ← Back to Google / Email sign in
              </Link>
            </Typography>
          </Stack>
        </Box>
      </Paper>
      
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
};

export default PasswordLoginPage;
